import type { User } from '../types/auth'

const DATABASE_NAME = 'domusaurea-offline'
const DATABASE_VERSION = 3
const SESSION_STORE = 'session'
const DATA_STORE = 'offline-data'
export const OUTBOX_STORE = 'offline-outbox'
const SESSION_KEY = 'last-authenticated-session'

export interface OfflineSessionSnapshot {
  user: User
  permissions: string[]
  refreshTokenExpiresAt: string
  savedAt: string
}

export function isOfflineSessionActive(snapshot: Pick<OfflineSessionSnapshot, 'refreshTokenExpiresAt'>, now = Date.now()): boolean {
  const expiresAt = Date.parse(snapshot.refreshTokenExpiresAt)
  return Number.isFinite(expiresAt) && expiresAt > now
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB non disponibile'))
      return
    }
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)
    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(SESSION_STORE)) database.createObjectStore(SESSION_STORE)
      if (!database.objectStoreNames.contains(DATA_STORE)) database.createObjectStore(DATA_STORE)
      if (!database.objectStoreNames.contains(OUTBOX_STORE)) database.createObjectStore(OUTBOX_STORE, { keyPath: 'noteId' })
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

export async function saveOfflineSession(snapshot: Omit<OfflineSessionSnapshot, 'savedAt'>): Promise<void> {
  const database = await openDatabase()
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction([SESSION_STORE, DATA_STORE, OUTBOX_STORE], 'readwrite')
    const sessionStore = transaction.objectStore(SESSION_STORE)
    const request = sessionStore.get(SESSION_KEY)
    request.onsuccess = () => {
      const previous = request.result as OfflineSessionSnapshot | undefined
      if (previous && (
        previous.user.id !== snapshot.user.id ||
        previous.user.agencyId !== snapshot.user.agencyId ||
        JSON.stringify(previous.permissions) !== JSON.stringify(snapshot.permissions)
      )) {
        transaction.objectStore(DATA_STORE).clear()
        transaction.objectStore(OUTBOX_STORE).clear()
      }
      sessionStore.put({ ...snapshot, savedAt: new Date().toISOString() }, SESSION_KEY)
    }
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
  database.close()
}

export async function readOfflineSession(): Promise<OfflineSessionSnapshot | null> {
  const database = await openDatabase()
  const snapshot = await new Promise<OfflineSessionSnapshot | null>((resolve, reject) => {
    const request = database.transaction(SESSION_STORE, 'readonly').objectStore(SESSION_STORE).get(SESSION_KEY)
    request.onsuccess = () => resolve((request.result as OfflineSessionSnapshot | undefined) ?? null)
    request.onerror = () => reject(request.error)
  })
  database.close()
  return snapshot
}

export async function clearOfflineSession(): Promise<void> {
  const database = await openDatabase()
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction([SESSION_STORE, DATA_STORE, OUTBOX_STORE], 'readwrite')
    transaction.objectStore(SESSION_STORE).delete(SESSION_KEY)
    transaction.objectStore(DATA_STORE).clear()
    transaction.objectStore(OUTBOX_STORE).clear()
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
  database.close()
}

export async function saveOfflineData<T>(key: string, data: T, owner: { userId: string; agencyId: string }): Promise<void> {
  const database = await openDatabase()
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction([SESSION_STORE, DATA_STORE], 'readwrite')
    const sessionRequest = transaction.objectStore(SESSION_STORE).get(SESSION_KEY)
    sessionRequest.onsuccess = () => {
      const session = sessionRequest.result as OfflineSessionSnapshot | undefined
      if (!session || !isOfflineSessionActive(session) || session.user.id !== owner.userId || session.user.agencyId !== owner.agencyId) return
      transaction.objectStore(DATA_STORE).put({
        userId: session.user.id,
        agencyId: session.user.agencyId,
        key,
        data,
        savedAt: new Date().toISOString(),
      }, `${session.user.id}:${session.user.agencyId}:${key}`)
    }
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
  database.close()
}

export async function readOfflineData<T>(key: string, owner?: { userId: string; agencyId: string }): Promise<{ data: T; savedAt: string } | null> {
  const session = await readOfflineSession()
  if (!session || !isOfflineSessionActive(session)) return null
  if (owner && (session.user.id !== owner.userId || session.user.agencyId !== owner.agencyId)) return null
  const database = await openDatabase()
  const record = await new Promise<{ data: T; savedAt: string } | null>((resolve, reject) => {
    const request = database.transaction(DATA_STORE, 'readonly').objectStore(DATA_STORE)
      .get(`${session.user.id}:${session.user.agencyId}:${key}`)
    request.onsuccess = () => resolve((request.result as { data: T; savedAt: string } | undefined) ?? null)
    request.onerror = () => reject(request.error)
  })
  database.close()
  return record
}

export function isNetworkUnavailable(error: unknown): boolean {
  if (typeof error === 'object' && error !== null && 'response' in error && (error as { response?: unknown }).response != null) {
    return false
  }
  if (!navigator.onLine) return true
  return typeof error === 'object' && error !== null && 'response' in error && (error as { response?: unknown }).response == null
}
